import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';

import { MODAL_DATA, MODAL_REF, ModalRef } from '@services/components/ModalService/modal.ref';
import { LandingService } from '@services/landing/landing.service';
import { AdminProduct } from '@services/products/interfaces/product.interfaces';
import { ProductService } from '@services/products/products.service';
import { SearchBarComponent } from '@shared/components/search-bar-component/search-bar.component';
import { STATUS } from '@shared/interfaces/global.interface';
import { ClpCurrencyPipe } from '@shared/pipes/clp-currency.pipe';

import { CDN_PRODUCTS_URL } from '@constants/constants';
import { LANDING_MANAGEMENT_TEXTS } from '@constants/text.constants';

export type SelectProductSlotType = 'featured' | 'plant';

export interface SelectProductModalData {
  slotType: SelectProductSlotType;
  slotIndex: number;
  currentProductId: string | null;
  occupiedProductIds: string[];
}

@Component({
  selector: 'app-select-product-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SearchBarComponent, ClpCurrencyPipe],
  templateUrl: './select-product-modal.component.html',
  styleUrl: './select-product-modal.component.css',
})
export class SelectProductModalComponent implements OnInit {
  private readonly modalRef = inject(MODAL_REF) as ModalRef<void>;
  private readonly data = inject<SelectProductModalData>(MODAL_DATA);
  private readonly productService = inject(ProductService);
  private readonly landingService = inject(LandingService);

  public readonly texts = LANDING_MANAGEMENT_TEXTS;
  public readonly products = signal<AdminProduct[]>([]);
  public readonly isLoading = signal(true);
  public readonly isSaving = signal(false);
  public readonly selectedProductId = signal<string | null>(this.data.currentProductId);

  public readonly filteredProducts = computed(() => {
    const term = this._searchTerm().toLowerCase().trim();
    const occupied = new Set(
      this.data.occupiedProductIds.filter(id => id !== this.data.currentProductId),
    );

    return this.products().filter((product) => {
      if (occupied.has(product.id)) {
        return false;
      }
      if (term) {
        return product.name.toLowerCase().includes(term);
      }
      return true;
    });
  });

  private readonly _searchTerm = signal('');

  ngOnInit(): void {
    this.loadProducts();
  }

  public isSelected(product: AdminProduct): boolean {
    return this.selectedProductId() === product.id;
  }

  public onSearch(term: string): void {
    this._searchTerm.set(term);
  }

  public onSelect(product: AdminProduct): void {
    this.selectedProductId.set(product.id);
    this.isSaving.set(true);

    const dto = this.buildUpdateDto(product.id);

    this.landingService.updateFeatured(dto).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.modalRef.close({ reason: 'confirm' });
      },
      error: () => {
        this.isSaving.set(false);
      },
    });
  }

  public onCancel(): void {
    this.modalRef.close({ reason: 'cancel' });
  }

  public getProductImageUrl(product: AdminProduct): string {
    if (!product.imageUrl) {
      return '';
    }
    if (/^https?:\/\//i.test(product.imageUrl)) {
      return product.imageUrl;
    }
    return `${CDN_PRODUCTS_URL}${product.imageUrl}`;
  }

  public isComingSoon(product: AdminProduct): boolean {
    return product.status === STATUS.COMING_SOON;
  }

  private buildUpdateDto(newProductId: string) {
    if (this.data.slotType === 'plant') {
      return { productId: newProductId, isMandala: true };
    }

    return { productId: newProductId, position: this.data.slotIndex + 1 };
  }

  private loadProducts(): void {
    this.productService.getAdminProducts(1, 50).subscribe((response) => {
      this.products.set(response.data);
      this.isLoading.set(false);
    });
  }
}
